import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

import { createAuthenticatedOtlpExporter } from './authenticated-otlp-exporter';
import { registerWebVitals } from './web-vitals';
import { environment } from '../../../environments/environment';

/**
 * viewer (Angular) の OpenTelemetry Web SDK 初期化モジュール。
 *
 * viewer が trace の root（document-load span / user-interaction span）を生成し、
 * HttpClient 経由の api-server 呼び出しに traceparent を伝播することで、
 * viewer → api-server → AI Server / DB を 1 trace で追跡する（E2E trace）。
 *
 * ## resource / sampler
 * backend の共通 helper（common/infrastructure/src/tracing/init-otel.ts）と揃え、
 * service.name を semantic-conventions の ATTR_SERVICE_NAME で "viewer" に設定する。
 * sampler は TraceIdRatioBasedSampler(environment.otel.samplingRatio)。backend は
 * ParentBasedSampler で「親の判定を継承」する構造のため、root を張る viewer 側の
 * この比率が trace 全体のサンプリング率を実質的に支配する（コスト制御ノブ）。
 * viewer は常に root（親を持たない）なので ParentBasedSampler でラップする必要はなく、
 * TraceIdRatioBasedSampler を直接使う。
 *
 * ## ZoneContextManager と Angular zone.js の共存
 * apps/viewer/project.json の build target は polyfills に "zone.js" を指定しており、
 * zone.js は main.ts のアプリコードより前に読み込まれる。ZoneContextManager は
 * その zone.js に相乗りして「非同期処理をまたいで active span を伝播」する。
 * Angular の provideZoneChangeDetection({ eventCoalescing: true }) も同じ zone.js を
 * 使うが、両者は同一の Zone 基盤を共有するだけで競合しない（OTel は span 用の
 * ContextManager として、Angular は変更検知トリガーとして zone を利用する）。
 * したがって initOtel() を bootstrapApplication より前に呼んでも問題ない。
 * むしろ document-load span を bootstrap 前から捕捉するため、前に呼ぶ必要がある。
 */

/**
 * OTel を初期化すべきか判定する（純粋関数、テスト対象）。
 *
 * useMock: true の環境（mock.interceptor が HTTP を横取りする）では、送信先の
 * api-server テレメトリ受け口が存在せず BatchSpanProcessor が送信エラーを繰り返すため、
 * SDK 初期化自体をスキップする。
 */
export function shouldInitOtel(env: { useMock: boolean }): boolean {
  return !env.useMock;
}

/**
 * apiUrl から OTLP traces 送信先 URL を組み立てる（純粋関数、テスト対象）。
 * 末尾スラッシュの有無を正規化し、`/telemetry/traces` を 1 個だけ付与する。
 */
export function buildTracesEndpoint(apiUrl: string): string {
  const base = apiUrl.replace(/\/+$/, '');
  return `${base}/telemetry/traces`;
}

/**
 * apiUrl を前方一致する RegExp を組み立てる（純粋関数、テスト対象）。
 *
 * FetchInstrumentation の propagateTraceHeaderCorsUrls に渡し、自 API 宛リクエストにのみ
 * traceparent を注入するために使う。apiUrl 内の正規表現メタ文字はエスケープし、
 * `^` を付けて前方一致にする（外部ドメイン・S3 presigned URL へ traceparent を漏らさない）。
 */
export function buildApiUrlPattern(apiUrl: string): RegExp {
  // 正規表現メタ文字をエスケープする（. や ? などが任意文字扱いされるのを防ぐ）。
  const escaped = apiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped}`);
}

/**
 * OTel Web SDK を初期化する。main.ts の bootstrapApplication より前に一度だけ呼ぶ。
 */
export function initOtel(): void {
  // mock 環境では送信先が存在しないため初期化自体をスキップする。
  if (!shouldInitOtel(environment)) {
    return;
  }

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'viewer',
    }),
    // viewer は trace の root。ここの比率が全体のサンプリング率を支配する。
    sampler: new TraceIdRatioBasedSampler(environment.otel.samplingRatio),
    // SDK 2.x では spanProcessors を constructor で渡す（旧 addSpanProcessor は非推奨）。
    // 認証付き exporter（送信直前に Cognito idToken を Authorization: Bearer で付与）を
    // BatchSpanProcessor でバッチ送信する。
    spanProcessors: [
      new BatchSpanProcessor(
        createAuthenticatedOtlpExporter(
          buildTracesEndpoint(environment.apiUrl),
        ),
      ),
    ],
  });

  // グローバル TracerProvider として登録しつつ、zone.js ベースの ContextManager を登録する。
  provider.register({
    contextManager: new ZoneContextManager(),
  });

  // apiUrl 宛リクエストにのみ traceparent を伝播させるパターン。
  const apiUrlPattern = buildApiUrlPattern(environment.apiUrl);

  registerInstrumentations({
    // register() で global にも登録されるが、順序に依存せず確実に紐付けるため明示的に渡す。
    tracerProvider: provider,
    instrumentations: [
      // 初期ページロードの Navigation Timing を span 化する。
      // ignoreNetworkEvents: true で各フェーズの timing を span event として付与するのを抑制し、
      // span 数・送信ペイロードを削減する（X-Ray では細粒度の network event は冗長なため）。
      new DocumentLoadInstrumentation({ ignoreNetworkEvents: true }),
      // クリック等のユーザー操作を root span 化する。zone.js があれば zone にパッチして
      // 操作起点の非同期処理（HttpClient 呼び出し等）を同一 trace に束ねる。
      new UserInteractionInstrumentation(),
      // fetch 用計装。Angular HttpClient は app.config.ts で withFetch() 未指定のため
      // XMLHttpRequest ベースで動作しており本計装は現状ほぼ効かないが、将来 withFetch() へ
      // 移行した際に外部ドメインへ traceparent を漏らさないための安全弁として、
      // propagateTraceHeaderCorsUrls を自 API 前方一致に限定して設定しておく。
      // 実際の traceparent 付与は traceparent.interceptor.ts（HttpInterceptor）が担う。
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [apiUrlPattern],
        ignoreNetworkEvents: true,
      }),
    ],
  });

  // Core Web Vitals（LCP / CLS / INP）を購読し、値が確定した時点で専用 span を生成して記録する。
  // instrumentation-document-load は Navigation Timing ベースで Web Vitals を含まないため、
  // web-vitals パッケージで別途計測する（provider 登録後に呼ぶ必要がある）。
  registerWebVitals();
}
